import React from 'react';
import { X, UserRound } from 'lucide-react';

/**
 * AdminDemographicsActiveUsersModal
 * ---------------------------------
 * Shown when an admin taps the green "active today" number on a state row
 * (or an expanded city row) in the Demographics table. Lists the
 * active-today users for that state/city. Clicking a user asks the parent
 * to open the existing AdminUserProfileModal for that user — the same
 * navigation used by Top Referrers and Search users.
 *
 * The user list is passed in directly (the /admin/demographics response
 * already includes activeUsers), so this modal does no fetching of its own.
 *
 * Props:
 *  - isOpen: bool
 *  - onClose: fn()
 *  - title: string — location label e.g. "Maharashtra" or "Maharashtra · Mumbai"
 *  - users: [{ id, username, name, profilePic }]
 *  - onSelectUser: fn(userId) — parent opens the profile modal
 */
const AdminDemographicsActiveUsersModal = ({
  isOpen,
  onClose,
  title,
  users = [],
  onSelectUser,
}) => {
  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-end sm:items-center justify-center p-0 sm:p-4"
      data-testid="admin-demographics-active-users-modal"
    >
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
      />

      <div className="relative w-full max-w-md rounded-t-[20px] sm:rounded-[20px] bg-white shadow-2xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-4 pt-4 pb-2 border-b border-[#ececec]">
          <div>
            <p className="text-[10px] uppercase tracking-wide text-green-600 font-semibold font-['DM_Sans']">
              Active Today
            </p>
            <h3 className="text-base font-bold text-[#111111] font-['Outfit']">
              {title}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="admin-demographics-active-users-close"
            className="p-1.5 rounded-full hover:bg-[#f4f4f4]"
          >
            <X className="w-4 h-4 text-[#666666]" />
          </button>
        </div>

        <div className="px-3 py-3 overflow-y-auto flex-1">
          {users.length === 0 ? (
            <p className="py-10 text-xs text-[#666666] font-['DM_Sans'] text-center">
              No active users here right now.
            </p>
          ) : (
            <div className="space-y-2">
              <p
                className="text-[11px] text-[#666666] font-['DM_Sans'] px-1"
                data-testid="admin-demographics-active-users-count"
              >
                {users.length} active user{users.length === 1 ? '' : 's'}
              </p>
              {users.map((u) => (
                <button
                  key={u.id}
                  type="button"
                  data-testid={`admin-demographics-active-users-row-${u.id}`}
                  onClick={() => onSelectUser?.(u.id)}
                  className="w-full rounded-[14px] border border-[#ececec] bg-white px-3 py-2.5 flex items-center gap-3 text-left hover:border-[#9333EA]/40 transition-colors"
                >
                  {u.profilePic ? (
                    <img
                      src={u.profilePic}
                      alt={u.username}
                      className="h-10 w-10 rounded-full object-cover"
                    />
                  ) : (
                    <div className="h-10 w-10 rounded-full bg-[#9333EA]/10 flex items-center justify-center flex-shrink-0">
                      <UserRound className="w-5 h-5 text-[#9333EA]" />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-[#111111] font-['Outfit'] truncate">
                      {u.username || u.name || 'Unknown user'}
                    </p>
                    {u.name && u.name !== u.username && (
                      <p className="text-xs text-[#666666] font-['DM_Sans'] truncate">
                        {u.name}
                      </p>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDemographicsActiveUsersModal;
